import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import { ReflectionService } from "../../services/ReflectionService";
import { fileWriter } from "../../utils/FileUtils";
import generateUuid from "../../utils/Uuid";

export async function fetchHelperTextForTemplate(templateSections) {
  const promiseList = templateSections.map((section) =>
    Files.getFileContent("response.html", "templateHelperText", section.id)
  );
  const helperTexts = await Promise.all(promiseList);
  return helperTexts;
}

export async function upsertReflection({
  reflectionText,
  author,
  contextId,
  reflectionId = null,
  authorId = null,
  actions = [],
  externalUserName = null,
}) {
  const upsertReflection = externalUserName
    ? ExternalService.addExternalReflectionForContext
    : ReflectionService.addReflectionForContext;
  const id = reflectionId ?? generateUuid();
  const newReflectionPost = await upsertReflection({
    reflectionId: id,
    contextId: contextId,
    author: author,
    actions: actions,
    userId: authorId,
  });

  const newReflection = {
    ddl_id: id,
    content: reflectionText,
    is_draft: false,
    metadata: { author: author },
    actions: actions,
    timestamp: new Date().toISOString(),
  };

  const reflectionFile = fileWriter(
    reflectionText,
    "response.html",
    "text/html"
  );
  Files.postPreSignedUrl(
    newReflectionPost.url,
    newReflectionPost.fields,
    reflectionFile
  );

  return newReflection;
}

export async function upsertReflectionFromTemplate({
  sections,
  author,
  contextId,
  templateId,
  reflectionId = null,
  authorId = null,
  actions = [],
  externalUserName = false,
}) {
  const upsertReflection = externalUserName
    ? ExternalService.addExternalReflectionForContext
    : ReflectionService.addReflectionForContext;
  const id = reflectionId ?? generateUuid();
  const promiseList = sections.map((section) =>
    upsertReflection({
      reflectionId: id,
      sectionId: section.id,
      templateId: templateId,
      contextId: contextId,
      author: author,
      actions: actions,
      userId: authorId,
    })
  );

  const newReflections = await Promise.all(promiseList);

  newReflections.forEach((newReflection, index) => {
    const reflectionFile = fileWriter(
      sections[index].content,
      `${sections[index].id}-${index + 1}response.html`,
      "text/html"
    );

    Files.postPreSignedUrl(
      newReflection.url,
      newReflection.fields,
      reflectionFile
    );
  });

  const newReflection = {
    ddl_id: id,
    metadata: { author: author },
    actions: [],
    timestamp: new Date().toISOString(),
    sections: sections.map((section) => ({
      id: section.id,
      content: section.content,
      header_question: section.header_question,
      helper_text: section.helper_text,
    })),
    from_template: true,
    template_id: templateId,
  };

  return newReflection;
}

export async function upsertReflectionDraft({
  reflectionText,
  author,
  authorId,
  contextId,
  reflectionId = null,
}) {
  const id = reflectionId ?? generateUuid();
  const newReflectionPost = await ReflectionService.createReflectionDraft({
    reflectionId: id,
    contextId: contextId,
    author: author,
    userId: authorId,
  });

  const newReflection = {
    ddl_id: id,
    content: reflectionText,
    metadata: { author: author },
    is_draft: true,
    actions: [],
    timestamp: new Date().toISOString(),
    name: "response.html",
  };

  const reflectionFile = fileWriter(
    reflectionText,
    "response.html",
    "text/html"
  );
  Files.postPreSignedUrl(
    newReflectionPost.url,
    newReflectionPost.fields,
    reflectionFile
  );

  return newReflection;
}

export async function upsertReflectionDraftFromTemplate({
  sections,
  author,
  authorId,
  contextId,
  templateId,
  reflectionId,
}) {
  const id = reflectionId ?? generateUuid();

  const promiseList = sections.map((refSection) =>
    ReflectionService.createReflectionDraft({
      reflectionId: id,
      sectionId: refSection.id,
      templateId: templateId,
      contextId: contextId,
      author: author,
      userId: authorId,
    })
  );

  const newReflectionDrafts = await Promise.all(promiseList);

  newReflectionDrafts.forEach((newDraft, index) => {
    const reflectionFile = fileWriter(
      sections[index].content,
      `${sections[index].id}-${index + 1}response.html`,
      "text/html"
    );

    Files.postPreSignedUrl(newDraft.url, newDraft.fields, reflectionFile);
  });

  const newReflection = {
    ddl_id: id,
    metadata: { author: name },
    actions: [],
    timestamp: new Date().toISOString(),
    sections: sections.map((section) => ({
      id: section.id,
      content: section.content,
      header_question: section.header_question,
      helper_text: section.helper_text,
    })),
    from_template: true,
    template_id: templateId,
    is_draft: true,
  };

  return newReflection;
}
